
import React, { useEffect, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAnalytics } from '@/hooks/useAnalytics';
import { toApiErrorCode } from '@/utils/analyticsErrorCode';
import { keycloakApi } from '@/integrations/keycloak/client';
import { UserForm } from './UserForm';
import { useAppSelector } from '@/store/hooks';
import { X } from 'lucide-react';
import { Button } from '@musa.tech/design-system';

interface CreateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GeneratorOption {
  id: number;
  name: string;
}

interface EstablishmentOption {
  id: number;
  name: string;
  parent_id: number | null;
  n_level: number | null;
}

export const CreateUserModal: React.FC<CreateUserModalProps> = ({
  isOpen,
  onClose
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { trackButtonClick, trackModalOpen, trackError } = useAnalytics();
  const { generators: rawGenerators, establishments: rawEstablishments } = useAppSelector((s) => s.establishmentAccess);

  // Entrada no funil por este caminho (drawer do estado vazio da lista). O outro
  // caminho — página dedicada — registra o mesmo evento (GR-712, Suposição 05).
  useEffect(() => {
    if (isOpen) {
      trackModalOpen({ modalName: 'Adicionar Usuário' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const generatorOptions = useMemo((): GeneratorOption[] => {
    return (rawGenerators ?? [])
      .map((generator) => ({
        id: parseInt(generator.id, 10),
        name: generator.name || '',
      }))
      .filter((generator) => !Number.isNaN(generator.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawGenerators]);

  const establishmentOptions = useMemo((): EstablishmentOption[] => {
    return (rawEstablishments ?? [])
      .map((establishment) => ({
        id: parseInt(establishment.id, 10),
        name: establishment.name || '',
        parent_id: establishment.parent_id,
        n_level:
          establishment.n_level != null
            ? Number(establishment.n_level)
            : null,
        generator_id:
          establishment.generator_id != null
            ? parseInt(establishment.generator_id, 10)
            : null,
      }))
      .filter((establishment) => !Number.isNaN(establishment.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawEstablishments]);

  const createUserMutation = useMutation({
    mutationFn: async (userData: {
      firstName?: string;
      lastName?: string;
      user_name?: string;
      email: string;
      phone_number?: string;
      permission_role: string;
      temp_password: string;
      generator_links: Array<{
        generator_id: number;
        role: string;
        include_children: boolean;
        establishment_ids?: number[];
      }>;
    }) => {
      if (!userData.generator_links.length) {
        throw new Error('Selecione ao menos um gerador antes de criar o usuário.');
      }

      const fullname = userData.firstName && userData.lastName
        ? `${userData.firstName.trim()} ${userData.lastName.trim()}`
        : userData.user_name || '';

      const createdUser = await keycloakApi.createUser({
        email: userData.email,
        fullname: fullname,
        phone: userData.phone_number || undefined,
        generator_links: userData.generator_links,
        password: userData.temp_password,
      });

      return createdUser;
    },
    onSuccess: () => {
      trackButtonClick({ buttonLabel: 'usuarios_criar_sucesso' });
      toast({
        title: 'Usuário criado com sucesso',
        description: 'O novo usuário foi adicionado ao sistema.',
      });
      queryClient.invalidateQueries({ queryKey: ['users-with-keycloak'] });
      queryClient.invalidateQueries({ queryKey: ['users-with-permissions'] });
      onClose();
    },
    onError: (error: Error & { code?: string }) => {
      // RN-05: só o código de lista fechada vai para o GA4 — `error.message` pode
      // conter o e-mail digitado, devolvido cru pela API do Keycloak.
      trackError({ errorType: 'api_error', errorCode: toApiErrorCode(error) });
      const code = error.code || '';
      // Customized error messages based on error type
      let title = 'Erro ao criar usuário';
      let description = error.message || 'Ocorreu um erro inesperado.';

      // Check for user already exists error
      if (code === 'USER_ALREADY_EXISTS') {
        title = '⚠️ Usuário já cadastrado';
      } else if (
        error.message?.toLowerCase().includes('já está cadastrado') ||
        error.message?.toLowerCase().includes('already registered') ||
        error.message?.toLowerCase().includes('already exists') ||
        error.message?.toLowerCase().includes('provavelmente já está cadastrado')
      ) {
        title = '⚠️ Usuário já cadastrado';
      } else if (error.message?.includes('Generator ID não encontrado')) {
        title = '⚠️ Gerador não selecionado';
      } else if (error.message?.includes('Failed to create auth user')) {
        if (error.message?.toLowerCase().includes('duplicate') || 
            error.message?.toLowerCase().includes('already')) {
          title = '⚠️ Usuário já cadastrado';
          description = 'Este email já está cadastrado na plataforma. Por favor, use outro email ou verifique se o usuário já existe na lista de usuários.';
        }
      }
      
      toast({
        title,
        description,
        variant: 'destructive',
        duration: 6000,
      });
    }
  });

  const handleSubmit = (data: {
    firstName?: string;
    lastName?: string;
    user_name?: string;
    email: string;
    phone_number?: string;
    permission_role: string;
    temp_password: string;
    generator_links: Array<{
      generator_id: number;
      role: string;
      include_children: boolean;
      establishment_ids?: number[];
    }>;
  }) => {
    createUserMutation.mutate(data);
  };

  const handleCancel = () => {
    trackButtonClick({ buttonLabel: 'usuarios_criar_formulario_incompleto' });
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={handleCancel}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-[50vw] max-w-[600px] bg-background border-l shadow-lg flex flex-col transition-transform duration-[280ms] ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div>
            <h2 className="text-lg font-semibold">Adicionar Novo Usuário</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Crie um novo usuário e defina suas permissões de acesso.
            </p>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={handleCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 min-h-0">
          <UserForm
            generatorOptions={generatorOptions}
            establishmentOptions={establishmentOptions}
            onSubmit={handleSubmit}
            isLoading={createUserMutation.isPending}
          />
        </div>
      </div>
    </>
  );
};
